import React, { useState, useEffect } from 'react';
import { ForemanArea } from '../../../../types/ForemanArea';
import { Activity } from '../../../../types/Activity';
import { TaskStatus } from '../../../../types/RoutineTask';
import { activityService } from '../../../../services/activityService';
import { routineTaskService } from '../../../../services/routineTaskService';
import { RoutineTask } from '../../../../types/RoutineTask';
import { usePermissions } from '../../../../hooks/usePermissions';
import { getFieldLockingState, getLockedFieldStyle, canEditInternalReview } from '../../../../utils/fieldLocking';
import { TaskStage } from '../../../../types/Task';
import { Form, Alert, Button, Modal } from '@/components/ui';

interface OverheadTaskModalProps {
  open: boolean;
  task?: RoutineTask | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const OverheadTaskModal: React.FC<OverheadTaskModalProps> = ({
  open,
  task,
  onCancel,
  onSuccess,
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    id: '',
    description: '',
    status: TaskStatus.Active,
    foremanAreaId: '',
    primaryActivityId: '',
  });
  const { hasPermission } = usePermissions();
  
  // Field locking logic based on task state and user permissions
  const canEditInternalReviewPermission = canEditInternalReview(hasPermission);
  
  const fieldLockingState = getFieldLockingState({
    taskStatus: task?.status as TaskStage,
    hasEditInternalReviewPermission: canEditInternalReviewPermission
  });
  
  const isTaskLocked = fieldLockingState.isLocked;
  const lockedFieldStyle = getLockedFieldStyle(isTaskLocked);
  
  const isReadOnly = !hasPermission('overhead:standing_edit') || isTaskLocked;

  useEffect(() => {
    const loadActivities = async () => {
      try {
        const data = await activityService.getActivitiesByClassification('overhead');
        setActivities(data);
      } catch (error) {
        console.error('Failed to load activities:', error);
      }
    };

    if (open) {
      loadActivities();
    }
  }, [open]);

  useEffect(() => {
    if (task && open) {
      setFormData({
        id: task.id.toString(),
        description: task.description || '',
        status: task.status as TaskStatus,
        foremanAreaId: task.foremanAreaId?.toString() || '',
        primaryActivityId: task.activities?.[0]?.id.toString() || '', 
      });
    } else if (!open) {
      setFormData({
        id: '',
        description: '',
        status: TaskStatus.Active,
        foremanAreaId: '',
        primaryActivityId: '',
      });
    }
  }, [task, open]);

  const handleSave = async () => {
    if (isReadOnly) return;
    
    try {
      const payload = {
        id: parseInt(formData.id),
        description: formData.description,
        type: 'overhead' as const,
        foremanAreaId: parseInt(formData.foremanAreaId),
        activities: [parseInt(formData.primaryActivityId)],
        status: formData.status,
        mode: 'specific' as const,
        labourAllocation: 100,
        materialAllocation: 100,
        equipmentAllocation: 100
      };

      if (task) {
        await routineTaskService.update(task.id, payload);
      } else {
        await routineTaskService.create(payload);
      }
      
      setError(null);
      onSuccess();
    } catch (error) {
      console.error(`Failed to ${task ? 'update' : 'create'} overhead task:`, error);
      setError(`Failed to ${task ? 'update' : 'create'} overhead task`);
    }
  };

  return (
    <Modal show={open} onHide={onCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{task ? (isTaskLocked ? 'View Overhead Task' : 'Edit Overhead Task') : 'Create Overhead Task'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className={task && isTaskLocked ? 'wrap-readonly-form' : ''}>
            {error && (
              <Alert variant="danger">
                {error}
              </Alert>
            )}
            <form>
              <div className="tw-mb-3">
                <Form.Label>Task ID</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.id}
                  onChange={(e) => !isReadOnly && setFormData({...formData, id: e.target.value})}
                  readOnly={isReadOnly}
                />
              </div>
              <div className="tw-mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  value={formData.description}
                  onChange={(e) => !isReadOnly && setFormData({...formData, description: e.target.value})}
                  readOnly={isReadOnly}
                />
              </div>
              <div className="tw-mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => !isReadOnly && setFormData({...formData, status: e.target.value as TaskStatus})}
                  disabled={isReadOnly}
                >
                  {Object.values(TaskStatus).map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="tw-mb-3">
                <Form.Label>Foreman Area</Form.Label>
                <Form.Select
                  value={formData.foremanAreaId}
                  onChange={(e) => !isReadOnly && setFormData({...formData, foremanAreaId: e.target.value})}
                  disabled={isReadOnly}
                >
                  <option value="">Select Foreman Area</option>
                  {/* Add foreman areas options here */}
                </Form.Select>
              </div>
              <div className="tw-mb-3">
                <Form.Label>Activity</Form.Label>
                <Form.Select
                  value={formData.primaryActivityId}
                  onChange={(e) => !isReadOnly && setFormData({...formData, primaryActivityId: e.target.value})}
                  disabled={isReadOnly}
                >
                  <option value="">Select Activity</option>
                  {activities.map(activity => (
                    <option key={activity.id} value={activity.id}>
                      {activity.activityName}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        {!isReadOnly && (
          <Button type="button" variant="primary" onClick={handleSave}>
            {task ? 'Update' : 'Save'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default OverheadTaskModal; 